// addFilterNode — VQE-Edit edit-action: insert a WHERE predicate into a
// specific group in the WHERE tree at `parentPath`, joined by `combinator`.
// Delegates to SR-Edit `add-where-node` via `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
// filter (WHERE / HAVING).

import type {
  BooleanClauseTarget,
  NodePath,
  WhereCombinator,
} from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx } from '../commitEdit';
import { formatFilterText, type FilterFormValue } from './filterHelpers';

/**
 * Insert a predicate into the group at `parentPath` in the WHERE (or, with
 * `clause: 'having'`, HAVING) tree, joined to the existing children by
 * `combinator`. The predicate is either literal text or a structured
 * `FilterFormValue` stringified via `formatFilterText` (mirrors `addFilter`).
 *
 * Returns without applying when `predicate` resolves to an empty string, or
 * when `parentPath` does not point to a group (SR-Edit returns no planned edits).
 */
export async function addFilterNode(
  parentPath: NodePath,
  predicate: string | FilterFormValue,
  combinator: WhereCombinator,
  ctx: CommitEditCtx,
  clause?: BooleanClauseTarget,
): Promise<void> {
  const text = (typeof predicate === 'string' ? predicate : formatFilterText(predicate)).trim();
  if (text.length === 0) return;
  await commitEdit({ op: 'add-where-node', parentPath, text, combinator, clause }, ctx);
}
